import React, { useState, useEffect } from "react";
import "./distribution.less";
import { dataMap } from "@/constants/distribution";
import { rebuildFormula } from "@/config/func";
import materialsDB from "@/constants/materialsDB";
import ResultDataTable from "./DistributionResultDataTable";
import TowerEffect from "@/Components/TowerEffect";
import EffCharts from "@/Components/TowerEffect/EffCharts";
import ShowBtns from "./DistributionShowBtn";
import { Modal, Table, Select } from "antd";
import dayjs from "dayjs";

// ======================
// ✅ 改动 1：新增单位映射
// ======================
export const unitMap = {
    T: ["C", "K"],
    P: ["Pa", "kPa", "MPa", "bar", "atm"],
    MC: ["mg/m³", "g/m³", "kg/m³"],
    GFR: ["kmol/h", "mol/h"],
    LFR: ["kmol/h", "mol/h"],
    MFR: ["kg/h"],
    EnthalpyV: ["J/mol", "kJ/mol"],
    EnthalpyL: ["J/mol", "kJ/mol"],
    VFR: ["m³/h"],
};

// ======================
// ✅ 改动 2：单位换算工具函数
// ======================
const convertValue = (
    value: number,
    field: string,
    fromUnit: string,
    toUnit: string
): number => {
    if (value === undefined || value === null || isNaN(value)) return 0;

    switch (field) {
        case "T":
            if (fromUnit === "K" && toUnit === "C") return value - 273.15;
            if (fromUnit === "C" && toUnit === "K") return value + 273.15;
            break;

        case "P":
            if (fromUnit === "Pa") {
                if (toUnit === "kPa") return value / 1000;
                if (toUnit === "MPa") return value / 1e6;
                if (toUnit === "bar") return value / 1e5;
                if (toUnit === "atm") return value / 101325;
            }
            if (fromUnit === "kPa" && toUnit === "Pa") return value * 1000;
            if (fromUnit === "MPa" && toUnit === "Pa") return value * 1e6;
            if (fromUnit === "bar" && toUnit === "Pa") return value * 1e5;
            if (fromUnit === "atm" && toUnit === "Pa") return value * 101325;
            break;

        case "GFR":
        case "LFR":
            if (fromUnit === "mol/h" && toUnit === "kmol/h") return value / 1000;
            if (fromUnit === "kmol/h" && toUnit === "mol/h") return value * 1000;
            break;

        case "EnthalpyV":
        case "EnthalpyL":
            if (fromUnit === "J/mol" && toUnit === "kJ/mol") return value / 1000;
            if (fromUnit === "kJ/mol" && toUnit === "J/mol") return value * 1000;
            break;

        case "MC":
            if (fromUnit === "mg/m³" && toUnit === "g/m³") return value / 1000;
            if (fromUnit === "mg/m³" && toUnit === "kg/m³") return value / 1e6;
            if (fromUnit === "g/m³" && toUnit === "kg/m³") return value / 1000;
            if (fromUnit === "g/m³" && toUnit === "mg/m³") return value * 1000;
            if (fromUnit === "kg/m³" && toUnit === "mg/m³") return value * 1e6;
            if (fromUnit === "kg/m³" && toUnit === "g/m³") return value * 1000;
            break;

        default:
            break;
    }
    return value;
};

const formatToSignificantDigits = (value: number, digits: number = 4): string => {
    if (value === 0) return "0";
    const exponent = Math.floor(Math.log10(Math.abs(value)));
    const factor = Math.pow(10, digits - 1 - exponent);
    return (Math.round(value * factor) / factor).toString();
};

const Distribution = (props: any) => {
    const { inputs, results, tempUnit, pressUnit, compareItems = [], selectTreeButton } = props;
    console.log("tp",tempUnit, pressUnit)
    const [currentMode, setCurrentMode] = useState<string>("P");
    const [currentModeData, setCurrentModeData] = useState<any[]>([]);
    const [currentMaterial, setCurrentMaterial] = useState<any>(null);
    const [currentData, setCurrentData] = useState<any>(null);
    const [effectTaskIndex, setEffectTaskIndex] = useState<number>(0); // 0=当前任务, 1+=对比任务索引
    const [showMode, setShowMode] = useState<"chart" | "table">("chart");
    const [modalStatus, setModalStatus] = useState<boolean>(false);
    const [modalTitle, setModalTitle] = useState<React.ReactNode>("");
    const [modalContent, setModalContent] = useState<React.ReactNode>("");

    // ======================
    // ✅ 改动 3：新增单位状态
    // ======================
    const [selectedUnits, setSelectedUnits] = useState({
        T: "C",
        P: "kPa",
        V: "mol/h",
        L: "mol/h",
        HV: "J/mol",
        HL: "J/mol",
        MC: "mg/m³",
    });

    // 外部统一温压单位同步到内部状态
    useEffect(() => {
        if (tempUnit) {
            setSelectedUnits(prev => ({
                ...prev,
                T: tempUnit === "℃" ? "C" : "K",
            }));
        }
    }, [tempUnit]);

    useEffect(() => {
        if (pressUnit) {
            setSelectedUnits(prev => ({
                ...prev,
                P: pressUnit === "KPa" ? "kPa" : pressUnit,
            }));
        }
    }, [pressUnit]);

    // ======================
    // ✅ 改动 4：单位切换事件
    // ======================
    const handleUnitChange = (field: string, unit: string) => {
        setSelectedUnits(prev => ({
            ...prev,
            [field]: unit,
        }));
    };

    useEffect(() => {
        if (results) {
            buildCurrentModeData();
        }
    }, [results, currentMode, selectedUnits]);

    // ======================
    // 对比数据：从 compareItems 提取当前塔的分布数据
    // ======================
    const compareDistData = React.useMemo(() => {
        if (!compareItems || compareItems.length === 0 || !selectTreeButton) return [];
        return compareItems
            .map((item: any) => {
                // 优先取各页面统一上报的输出元件数据；单元操作页面上报在 mix_input.elements
                const elements = item.outputElements || item.values?.mix_input?.elements || [];
                const elem = elements.find((e: any) => e.name === selectTreeButton);
                if (!elem?.result) return null;
                const baseName = item.values?.name || `对比任务${item.id}`;
                const taskName = item.type === "otherCalc"
                    ? `${baseName} (${item.created_at ? dayjs(item.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss") : ""})`
                    : baseName;
                return {
                    taskId: item.id,
                    taskName,
                    type: item.type,
                    result: elem.result,
                };
            })
            .filter(Boolean);
    }, [compareItems, selectTreeButton]);

    useEffect(() => {
        if (currentData !== null && currentData !== undefined) {
            buildModalSource();
        }
    }, [
        currentData,
        selectedUnits,
        effectTaskIndex,
        compareDistData,
    ]);

    // 构建 EffCharts 的 multiTaskData（当前任务 + 对比任务）
    const chartMultiTaskData = React.useMemo(() => {
        if (compareDistData.length === 0) return null;
        const tasks = compareDistData
            .filter((d: any) => d.result && d.result[currentMode] != null)
            .map((d: any) => {
                let data = d.result[currentMode] || [];
                if (["X", "Y", "K"].includes(currentMode)) {
                    if (currentMaterial && Array.isArray(data)) {
                        data = data.map((item: any) =>
                            Array.isArray(item) && currentMaterial.index != null && currentMaterial.index < item.length
                                ? item[currentMaterial.index] : 0
                        );
                    }
                } else if (["HV", "HL"].includes(currentMode) && Array.isArray(data)) {
                    data = data.map((v: any) => Array.isArray(v) && v.length > 0 ? v[0] : v);
                } else if (currentMode === "T" && Array.isArray(data)) {
                    data = data.map((v: number) => convertValue(v, "T", "K", selectedUnits.T));
                } else if (currentMode === "P" && Array.isArray(data)) {
                    data = data.map((v: number) => convertValue(v, "P", "Pa", selectedUnits.P));
                }
                return { taskId: d.taskId, taskName: d.taskName, data };
            });
        if (tasks.length === 0) return null;
        return [
            { taskId: "current", taskName: "当前任务", data: currentModeData },
            ...tasks,
        ];
    }, [compareDistData, currentMode, currentModeData, selectedUnits, currentMaterial]);

    // 构建表格的对比列数据（与 compareDistData 对齐）
    const compareTableData = React.useMemo(() => {
        if (compareDistData.length === 0) return [];
        return compareDistData.map((d: any) => {
            let data = d.result[currentMode] || [];
            if (["X", "Y", "K"].includes(currentMode)) {
                if (currentMaterial && Array.isArray(data)) {
                    data = data.map((item: any) =>
                        Array.isArray(item) && currentMaterial.index != null && currentMaterial.index < item.length
                            ? item[currentMaterial.index] : 0
                    );
                }
            } else if (["HV", "HL"].includes(currentMode) && Array.isArray(data)) {
                data = data.map((v: any) => Array.isArray(v) && v.length > 0 ? v[0] : v);
            } else if (currentMode === "T" && Array.isArray(data)) {
                data = data.map((v: number) => convertValue(v, "T", "K", selectedUnits.T));
            } else if (currentMode === "P" && Array.isArray(data)) {
                data = data.map((v: number) => convertValue(v, "P", "Pa", selectedUnits.P));
            }
            return { taskId: d.taskId, taskName: d.taskName, data };
        });
    }, [compareDistData, currentMode, selectedUnits, currentMaterial]);

    const hasCompareData = compareTableData.length > 0;

    // 塔板效果图所有数据（当前任务 + 各对比任务）
    const allEffectData = React.useMemo(() => {
        const prepareData = (raw: any[], needsComponentExtraction: boolean) => {
            if (!raw || !Array.isArray(raw)) return [];
            let data = raw;
            if (needsComponentExtraction && ["X", "Y", "K"].includes(currentMode)) {
                if (currentMaterial) {
                    data = data.map((item: any) =>
                        Array.isArray(item) && currentMaterial.index != null && currentMaterial.index < item.length
                            ? item[currentMaterial.index] : 0
                    );
                }
            } else if (["HV", "HL"].includes(currentMode)) {
                data = data.map((v: any) => Array.isArray(v) && v.length > 0 ? v[0] : v);
            } else if (currentMode === "T") {
                data = data.map((v: number) => convertValue(v, "T", "K", selectedUnits.T));
            } else if (currentMode === "P") {
                data = data.map((v: number) => convertValue(v, "P", "Pa", selectedUnits.P));
            }
            return data;
        };
        const tasks: { taskName: string; data: number[] }[] = [
            { taskName: "当前任务", data: prepareData(currentModeData, false) },
        ];
        if (hasCompareData) {
            compareDistData.forEach((d: any) => {
                if (d.result?.[currentMode]) {
                    tasks.push({ taskName: d.taskName, data: prepareData(d.result[currentMode], true) });
                }
            });
        }
        return tasks;
    }, [hasCompareData, currentMode, compareDistData, currentModeData, selectedUnits]);

    const buildComponentData = () => {
        const tmp = results[currentMode];
        if (!tmp || !Array.isArray(tmp) || !currentMaterial) {
            return [];
        }
        const { index } = currentMaterial;
        const componentData: any[] = [];

        // 遍历所有塔板，提取当前组分数据
        tmp.forEach((item: any) => {
            if (Array.isArray(item) && index >= 0 && index < item.length) {
                componentData.push(item[index]);
            }
        });
        return componentData;
    };

    useEffect(() => {
        if (["X", "Y", "K"].includes(currentMode) && currentMaterial) {
            const data = buildComponentData();
            setCurrentModeData(data);
        }
    }, [currentMaterial, currentMode]);

    // 构建当前模式的数据（含T/P单位换算）
    const buildCurrentModeData = () => {
        if (!results) return;

        // 非组分数据（T/P/V/L等）
        if (!["X", "Y", "K"].includes(currentMode)) {
            const data = results[currentMode] || [];

            // 处理HV/HL的二维数组转换
            if (["HV", "HL"].includes(currentMode) && Array.isArray(data)) {
                const flatData = data.map(item => Array.isArray(item) && item.length > 0 ? item[0] : item);
                setCurrentModeData(flatData);
            } else if (currentMode === "T" && Array.isArray(data)) {
                const converted = data.map(v => convertValue(v, "T", "K", selectedUnits.T));
                setCurrentModeData(converted);
            } else if (currentMode === "P" && Array.isArray(data)) {
                const converted = data.map(v => convertValue(v, "P", "Pa", selectedUnits.P));
                setCurrentModeData(converted);
            } else {
                setCurrentModeData(Array.isArray(data) ? data : []);
            }
        }
        // 组分数据（X/Y/K）- 设置默认组分
        else {
            if (inputs?.materials && inputs.materials.length > 0) {
                // 优先选择甲醇（CAS: 67-56-1）
                const methanol = inputs.materials.find((item: any) => item.cas === "67-56-1");
                setCurrentMaterial(methanol || inputs.materials[0]);
            }
        }
    };

    // 弹窗内容构建（核心）
    const buildModalSource = () => {
        if (currentData === null) return;
        const activeResults = effectTaskIndex === 0 ? results : (compareDistData[effectTaskIndex - 1]?.result || results);
        if (!activeResults) return;

        const activeTaskName = effectTaskIndex === 0 ? "当前任务" : (compareDistData[effectTaskIndex - 1]?.taskName || "对比任务");

        const title = (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 500 }}>
                    {activeTaskName} 塔板 {Number(currentData) + 1} 数据
                </span>
                <Select
                    value={currentData}
                    onChange={(val) => setCurrentData(val)}
                    style={{ width: 160 }}
                >
                    {currentModeData.map((_, index) => (
                        <Select.Option key={index} value={index}>
                            塔板 {index + 1}
                        </Select.Option>
                    ))}
                </Select>
            </div>
        );

        // 弹窗内容
        const content = (
            <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                <div style={{ marginBottom: 20 }}>
                    <h4 style={{ marginBottom: 10 }}>基础参数</h4>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 10,
                        }}
                    >
                        {Object.keys(dataMap)
                            .filter((key) => !["K", "X", "Y"].includes(key))
                            .map((key) => {
                                // ======================
                                // ✅ 改动 6：获取原始值 & 单位
                                // ======================
                                const rawValue = activeResults[key]?.[currentData];
                                const rawUnit = dataMap[key].unit;
                                let field = key;

                                if (key === "V") field = "GFR";
                                if (key === "L") field = "LFR";
                                if (key === "HV") field = "EnthalpyV";
                                if (key === "HL") field = "EnthalpyL";

                                const targetUnit = selectedUnits[field] || rawUnit;
                                const convertedValue = convertValue(
                                    rawValue,
                                    field,
                                    rawUnit,
                                    targetUnit
                                );

                                // ======================
                                // ✅ 改动 7：渲染带下拉选择器的参数项
                                // ======================
                                return (
                                    <div
                                        key={key}
                                        style={{
                                            padding: 8,
                                            border: "1px solid #f0f0f0",
                                            borderRadius: 4,
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "center",  // 新增：垂直居中对齐
                                            justifyContent: "space-between",  // 新增：两端对齐
                                        }}
                                    >
                                        {/* 左侧：标题 + 数据 */}
                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            <span style={{ color: "#666" }}>{dataMap[key]?.title}:&nbsp;</span>
                                            <span style={{ color: "#0148e0" }}>
                                                {/* {formatToSignificantDigits(convertedValue)} */}
                                                {convertedValue}
                                            </span>
                                        </div>

                                        {/* 右侧：单位选择器 */}
                                        {unitMap[field as keyof typeof unitMap] && (
                                            <Select
                                                size="small"
                                                value={targetUnit}
                                                onChange={(unit) => handleUnitChange(field, unit)}
                                                style={{ width: 90 }}
                                            >
                                                {unitMap[field as keyof typeof unitMap].map((u) => (
                                                    <Select.Option key={u} value={u}>
                                                        {u}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                </div>

                {/* 组分表格 */}
                <div>
                    <h4 style={{ marginBottom: 10 }}>组分数据</h4>
                    <Table
                        size="small"
                        bordered
                        pagination={false}
                        columns={[
                            { title: "组分", dataIndex: "name", width: 180 },
                            { title: "液相摩尔分率", dataIndex: "x" },
                            { title: "汽相摩尔分率", dataIndex: "y" },
                            { title: "相平衡常数", dataIndex: "k" },
                            { title: "摩尔流量(mol/h)", dataIndex: "molFlow" },
                            { title: "质量流量(g/h)", dataIndex: "massFlow" },
                            { title: "质量浓度(mg/Nm³)", dataIndex: "massCon" },
                        ]}
                        dataSource={inputs.materials.map((material, index) => {
                            const xValue = activeResults["X"]?.[currentData]?.[index] ?? "-";
                            const yValue = activeResults["Y"]?.[currentData]?.[index] ?? "-";
                            const kValue = activeResults["K"]?.[currentData]?.[index] ?? "-";

                            const liq = activeResults["L"]?.[currentData] ?? 0;
                            const vap = activeResults["V"]?.[currentData] ?? 0;
                            const t = activeResults["T"]?.[currentData] ?? 298.15;
                            const p = activeResults["P"]?.[currentData] ?? 101325;

                            let molFlow = "-";
                            if (xValue !== "-") {
                                molFlow = formatToSignificantDigits(Number(xValue) * liq);
                            } else if (yValue !== "-") {
                                molFlow = formatToSignificantDigits(Number(yValue) * vap);
                            }

                            let massFlow = "-";
                            if (xValue !== "-") {
                                massFlow = formatToSignificantDigits(Number(xValue) * liq * material.MW / 1000);
                            } else if (yValue !== "-") {
                                massFlow = formatToSignificantDigits(Number(yValue) * vap * material.MW / 1000);
                            }

                            let massCon = "-";
                            if (xValue !== "-") {
                                massCon = formatToSignificantDigits(
                                    (Number(xValue) * p * material.MW) / (8.314 * t)
                                );
                            } else if (yValue !== "-") {
                                massCon = formatToSignificantDigits(
                                    (Number(yValue) * p * material.MW) / (8.314 * t)
                                );
                            }

                            return {
                                key: index,
                                name: `${rebuildFormula(material.Molecular_formula)}（${material.Name_ZH}）`,
                                x: xValue === "-" ? "-" : formatToSignificantDigits(Number(xValue)),
                                y: yValue === "-" ? "-" : formatToSignificantDigits(Number(yValue)),
                                k: kValue,
                                molFlow,
                                massFlow,
                                massCon,
                            };
                        })}
                    />
                </div>
            </div>
        );

        setModalTitle(title);
        setModalContent(content);
    };

    // 汇总当前任务 + 对比任务的全部数值（用于纵坐标范围，避免对比数据超出坐标轴）
    const getAllAxisData = () => {
        const tasks = chartMultiTaskData && Array.isArray(chartMultiTaskData)
            ? chartMultiTaskData.map((t: any) => t.data)
            : [currentModeData];
        return tasks.flat().filter((v: any) => !isNaN(Number(v)) && isFinite(Number(v)));
    };

    const getMaxValue = () => {
        const valid = getAllAxisData();
        if (valid.length === 0) return 1;
        const max = Math.max(...valid);
        const min = Math.min(...valid);
        const range = Math.abs(max - min);
        return max + range * 0.1;
    };

    const getMinValue = () => {
        const valid = getAllAxisData();
        if (valid.length === 0) return 0;
        const max = Math.max(...valid);
        const min = Math.min(...valid);
        const range = Math.abs(max - min);
        return min >= 0 ? Math.max(0, min - range * 0.1) : min - range * 0.1;
    };

    const getChartTitle = () => {
        if (["K", "X", "Y"].includes(currentMode) && currentMaterial) {
            return `${rebuildFormula(currentMaterial?.Molecular_formula)}${dataMap[currentMode]?.title || ""}`;
        } else {
            return `${dataMap[currentMode]?.title || currentMode}分布图`;
        }
    };

    const showMF = (item: any) => {
        const mat = materialsDB.find(i => i.cas === item.cas);
        return mat ? rebuildFormula(mat.Molecular_formula) : rebuildFormula(item.Molecular_formula || item.name);
    };

    const isLoading = !currentModeData || currentModeData.length === 0;
    console.log("currentModeData",currentModeData)

    return (
        <div className="single-result-container">
            <div className="show-btns-wrapper">
                <ShowBtns
                    currentMode={currentMode}
                    setCurrentMode={setCurrentMode}
                    showMode={showMode}
                    setShowMode={setShowMode}
                    setCurrentData={setCurrentData}
                />
            </div>

            {isLoading ? (
                <div className="loading-area">
                    <p>正在加载数据...</p>
                    <p className="loading-mode">当前模式: {currentMode}</p>
                </div>
            ) : (
                <div className="main-content-wrapper" style={hasCompareData ? { flexDirection: "column" } : undefined}>
                    {/* 左侧塔板效果图 */}
                    {hasCompareData ? (
                        <div className="tower-effect-wrapper" style={{ width: "100%", overflowX: "auto", display: "flex", justifyContent: "center" }}>
                            <div style={{ display: "flex", gap: 24, flexShrink: 0 }}>
                                {allEffectData.map((task: any, ti: number) => (
                                    <div key={ti} style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, color: ti === 0 ? "#0148e0" : "#ffa318" }}>
                                            {ti === 0 ? "当前任务" : task.taskName}
                                        </div>
                                        <TowerEffect
                                            currentMode={currentMode}
                                            setModalStatus={setModalStatus}
                                            currentData={currentData}
                                            setCurrentData={(idx: number) => { setEffectTaskIndex(ti); setCurrentData(idx); }}
                                            currentModeData={task.data}
                                            wrapWidth={280}
                                            trayHeight={310}
                                            imageStyle={{ height: "420px", width: "80px" }}
                                            trayContainerStyle={{ top: "40px", left: "27px", width: "47px" }}
                                            unit={["T", "P"].includes(currentMode) ? selectedUnits[currentMode as "T" | "P"] : undefined}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="tower-effect-wrapper">
                            <TowerEffect
                                currentMode={currentMode}
                                setModalStatus={setModalStatus}
                                currentData={currentData}
                                setCurrentData={(idx: number) => { setEffectTaskIndex(0); setCurrentData(idx); }}
                                currentModeData={currentModeData}
                                wrapWidth={280}
                                trayHeight={310}
                                imageStyle={{ height: "420px", width: "80px" }}
                                trayContainerStyle={{ top: "40px", left: "27px", width: "47px" }}
                                unit={["T", "P"].includes(currentMode) ? selectedUnits[currentMode as "T" | "P"] : undefined}
                            />
                        </div>
                    )}

                    <div className="data-show-area" style={hasCompareData ? { width: "100%", display: "flex", justifyContent: "center", marginLeft: 0 } : undefined}>
                        <div className="line-chart-container">
                            {["X", "Y", "K"].includes(currentMode) && inputs?.materials && (
                                <div className="materials-select-container">
                                    {inputs.materials.map((i: any, index) => {
                                        const isActive = currentMaterial?.cas === i.cas;
                                        return (
                                            <div key={index} className="material-item">
                                                <button
                                                    className={`material-btn ${isActive ? "active" : ""}`}
                                                    onClick={() => setCurrentMaterial({ ...i, index })}
                                                >
                                                    <span className={`material-btn-text ${isActive ? "active" : ""}`}>
                                                        {showMF(i)}
                                                    </span>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {showMode === "chart" ? (
                                <EffCharts
                                    // 对比状态切换时容器宽度变化，通过 key 强制重建图表实例以适配新宽度
                                    key={hasCompareData ? "multi-task" : "single-task"}
                                    chartData={currentModeData}
                                    multiTaskData={chartMultiTaskData}
                                    title={`塔板${getChartTitle()}`}
                                    currentMode={currentMode}
                                    height={470}
                                    min={getMinValue()}
                                    max={getMaxValue()}
                                    unit={["T", "P"].includes(currentMode) ? selectedUnits[currentMode as "T" | "P"] : undefined}
                                />
                            ) : (
                                <ResultDataTable
                                    results={results}
                                    inputMaterials={inputs.materials}
                                    currentMode={currentMode}
                                    currentModeData={currentModeData}
                                    currentMaterial={currentMaterial}
                                    compareTableData={compareTableData}
                                    unit={["T", "P"].includes(currentMode) ? selectedUnits[currentMode as "T" | "P"] : undefined}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            <Modal
                title={modalTitle}
                open={modalStatus}
                onCancel={() => {
                    setModalStatus(false);
                    setCurrentData(null);
                }}
                width={1300}
                destroyOnClose
                zIndex={100002}
            >
                {modalContent}
            </Modal>
        </div>
    );
};

export default Distribution;